import React from "react";

const PURPLE = "#7F56D9";
const PURPLE_SOFT = "#F4EBFF";
const BORDER = "#E4E7EC";
const DASHED = "#D0D5DD";

/** Features come from the Stripe product metadata when present, so plan copy
 *  can be edited in Stripe without a deploy. */
const DEFAULT_FEATURES = [
  "Unlimited reports",
  "Integrations & API access",
  "Monthly billing, cancel any time",
  "Email support",
  "Data export",
];

const featuresFor = (plan) => {
  const raw = plan?.metadata?.features;

  if (Array.isArray(raw) && raw.length) {
    return raw;
  }

  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split("|")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return DEFAULT_FEATURES;
};

const CURRENCY_SYMBOLS = { USD: "$", EUR: "\u20AC", GBP: "\u00A3", INR: "\u20B9" };

const formatPrice = (amount, currency = "USD") => {
  if (amount == null || Number.isNaN(Number(amount))) {
    return null;
  }

  const code = String(currency ?? "USD").toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] ?? "";
  const value = Number(amount);
  const shown = Number.isInteger(value) ? `${value}` : value.toFixed(2);

  return symbol ? `${symbol}${shown}` : `${shown} ${code}`;
};

/** "month" / "year" straight from the Stripe price. */
const intervalLabel = (price) => {
  const count = price?.recurring?.interval_count ?? 1;
  const interval = price?.recurring?.interval ?? "month";

  return count > 1 ? `${count} ${interval}s` : interval;
};

/** "Sep 12, 2026" from an API date string; null when unusable. */
const formatDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(String(value).replace(" ", "T"));

  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const Stat = ({ label, value, sub = null, last = false }) => (
  <div
    className="flex-fill p-3"
    style={{ borderRight: last ? "none" : `1px solid ${BORDER}`, minWidth: 150 }}
  >
    <div
      className="fw-semibold mb-1"
      style={{ color: "#98A2B3", fontSize: "0.72rem", letterSpacing: "0.06em" }}
    >
      {label}
    </div>
    <div className="fw-bold" style={{ color: "#101828" }}>
      {value}
    </div>
    {sub ? (
      <div className="text-muted" style={{ fontSize: "0.85rem" }}>
        {sub}
      </div>
    ) : null}
  </div>
);

const CheckIcon = () => (
  <span
    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
    style={{
      width: 22,
      height: 22,
      borderRadius: 6,
      background: PURPLE_SOFT,
      color: PURPLE,
    }}
  >
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  </span>
);

const SideCard = ({ title, dashed = false, action = null, children }) => (
  <div
    className="rounded-3 p-4 mb-3"
    style={{
      border: dashed ? `1px dashed ${DASHED}` : `1px solid ${BORDER}`,
      background: dashed ? "#FCFAFF" : "#FFFFFF",
    }}
  >
    <div className="d-flex align-items-center justify-content-between mb-2">
      <div
        className="fw-semibold"
        style={{ color: "#98A2B3", fontSize: "0.75rem", letterSpacing: "0.06em" }}
      >
        {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

/**
 * Subscription tab for a single-plan product: state banner, the plan card with
 * price/features/subscribe, and a side column for payment method + invoices.
 */
const SubscriptionOverview = ({
  plan,
  subscription,
  cards = [],
  loading = false,
  subscribing = false,
  onSubscribe,
  onCancel,
  cancelling = false,
  onToggleAutoRenew,
  autoRenewPending = false,
  onAddPaymentMethod,
  onViewInvoices,
  invoices = [],
  invoiceCount = 0,
}) => {
  const status = (subscription?.status ?? "").toLowerCase();
  const isLive = ["active", "trialing"].includes(status);

  const priceObject = plan?.prices?.[0] ?? null;
  const currency = String(priceObject?.currency ?? "USD").toUpperCase();
  const price = formatPrice(priceObject?.amount, currency);
  const period = intervalLabel(priceObject);
  const planName = plan?.name ?? "Basic";
  const features = featuresFor(plan);
  const defaultCard = cards.find((card) => card?.is_default) ?? cards[0] ?? null;

  // "Started" is when they first subscribed - created_at. current_period_start
  // moves forward on every renewal, so it would show this month, not the start.
  const startedOn = formatDate(
    subscription?.created_at ?? subscription?.current_period_start,
  );
  const nextChargeOn = formatDate(subscription?.current_period_end);
  const cycleLabel = period === "month" ? "Monthly" : `Every ${period}`;

  // auto_renew is read live from Stripe; null means Stripe couldn't be reached,
  // so show "Unavailable" instead of implying a state we don't actually know.
  const autoRenewUnknown =
    subscription?.auto_renew === null || subscription?.auto_renew === undefined;
  const autoRenewOn = subscription?.auto_renew === true;

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" style={{ color: PURPLE }} role="status" />
        <p className="mt-3 small text-muted">Loading your plan...</p>
      </div>
    );
  }

  return (
    <div>
      {/* State banner */}
      {!isLive && (
        <div
          className="d-flex align-items-start gap-3 rounded-3 p-4 mb-4"
          style={{ border: `1px dashed ${DASHED}`, background: "#FCFCFD" }}
        >
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: PURPLE_SOFT,
              color: PURPLE,
              fontSize: "1.2rem",
            }}
          >
            !
          </span>
          <div>
            <div className="fw-bold mb-1" style={{ color: "#101828" }}>
              You don't have an active subscription
            </div>
            <div className="text-muted" style={{ fontSize: "0.95rem" }}>
              Members can't access reports until a plan is active. Subscribe
              below to restore access immediately.
            </div>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Plan card */}
        <div className="col-lg-8">
          <div
            className="rounded-3 h-100"
            style={{ border: `1px solid ${BORDER}`, background: "#FCFAFF" }}
          >
            <div className="p-4">
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span
                    className="rounded-2 px-3 py-1 fw-bold"
                    style={{
                      background: PURPLE_SOFT,
                      color: PURPLE,
                      fontSize: "0.8rem",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {planName.toUpperCase()}
                  </span>

                  {isLive && (
                    <span
                      className="rounded-2 px-3 py-1 fw-semibold"
                      style={{
                        background: "#ECFDF3",
                        color: "#027A48",
                        fontSize: "0.8rem",
                      }}
                    >
                      {status === "trialing" ? "Trial" : "Active"}
                    </span>
                  )}
                </div>

                {isLive ? (
                  <button
                    type="button"
                    className="btn px-3 py-2 rounded-3 fw-semibold"
                    style={{
                      background: "#FFFFFF",
                      color: "#B42318",
                      border: "1px solid #FDA29B",
                      whiteSpace: "nowrap",
                    }}
                    disabled={cancelling}
                    onClick={onCancel}
                  >
                    {cancelling ? "Cancelling..." : "Cancel subscription"}
                  </button>
                ) : (
                  <div className="text-end">
                    <div
                      className="fw-semibold"
                      style={{
                        color: "#98A2B3",
                        fontSize: "0.75rem",
                        letterSpacing: "0.06em",
                      }}
                    >
                      STATUS
                    </div>
                    <div className="fw-bold" style={{ color: "#101828" }}>
                      No active plan
                    </div>
                  </div>
                )}
              </div>

              <div className="d-flex align-items-baseline gap-2 mt-3">
                <span
                  className="fw-bold"
                  style={{ fontSize: "2.75rem", color: "#101828", lineHeight: 1 }}
                >
                  {price ?? "—"}
                </span>
                <span className="text-muted" style={{ fontSize: "1.1rem" }}>
                  / {period}
                </span>
              </div>

              <p className="text-muted mt-3 mb-0" style={{ fontSize: "1rem" }}>
                {plan?.description ??
                  "Our single plan — everything your team needs. Cancel any time, no commitment beyond the current month."}
              </p>
            </div>

            {isLive ? (
              <div style={{ borderTop: `1px solid ${BORDER}` }} className="p-4">
                <div
                  className="rounded-3 d-flex flex-wrap"
                  style={{ border: `1px solid ${BORDER}`, background: "#FFFFFF" }}
                >
                  <Stat label="STARTED" value={startedOn ?? "—"} />
                  <Stat
                    label="NEXT CHARGE"
                    value={nextChargeOn ?? "—"}
                    sub={price ? `${price} ${currency}` : null}
                  />
                  <Stat label="BILLING CYCLE" value={cycleLabel} last />
                </div>

                <div
                  className="d-flex justify-content-between align-items-center gap-3 mt-4 pt-4"
                  style={{ borderTop: `1px solid ${BORDER}` }}
                >
                  <div>
                    <div className="fw-bold" style={{ color: "#101828" }}>
                      Auto-renewal
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      {autoRenewUnknown
                        ? subscription?.stripe_error ||
                          "Couldn't read this from Stripe. Reload to try again."
                        : autoRenewOn
                          ? `${
                              nextChargeOn
                                ? `Renews on ${nextChargeOn}`
                                : "Renews automatically"
                            }${
                              defaultCard
                                ? ` \u00B7 charged automatically to \u2022\u2022\u2022\u2022 ${defaultCard.last4}`
                                : ""
                            }`
                          : `Off \u00B7 no further charges. Your plan stays active until ${
                              nextChargeOn ?? "the end of the current period"
                            }.`}
                    </div>
                  </div>

                  {autoRenewUnknown ? (
                    <span
                      className="rounded-pill px-3 py-1 fw-semibold flex-shrink-0"
                      style={{
                        background: "#FFFAEB",
                        color: "#B54708",
                        fontSize: "0.78rem",
                      }}
                    >
                      Unavailable
                    </span>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={autoRenewOn}
                      aria-label="Auto-renewal"
                      onClick={() => onToggleAutoRenew?.(!autoRenewOn)}
                      disabled={autoRenewPending}
                      style={{
                        width: 56,
                        height: 32,
                        borderRadius: 999,
                        border: "none",
                        background: autoRenewOn ? PURPLE : "#D0D5DD",
                        position: "relative",
                        cursor: autoRenewPending ? "wait" : "pointer",
                        opacity: autoRenewPending ? 0.6 : 1,
                        transition: "background 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          top: 4,
                          left: autoRenewOn ? 28 : 4,
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: "#FFFFFF",
                          transition: "left 0.2s",
                        }}
                      />
                    </button>
                  )}
                </div>
              </div>
            ) : (
            <div style={{ borderTop: `1px solid ${BORDER}` }} className="p-4">
              <div className="row g-3">
                {features.map((feature) => (
                  <div className="col-sm-6" key={feature}>
                    <div className="d-flex align-items-start gap-2">
                      <CheckIcon />
                      <span style={{ color: "#344054" }}>{feature}</span>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="btn w-100 mt-4 py-3 rounded-3 fw-bold"
                style={{
                  background: isLive ? "#E9D7FE" : PURPLE,
                  color: isLive ? "#6941C6" : "#FFFFFF",
                  border: "none",
                }}
                disabled={isLive || subscribing || !price}
                onClick={onSubscribe}
              >
                {isLive
                  ? "Current plan"
                  : subscribing
                    ? "Processing..."
                    : `Subscribe \u00B7 ${price ?? ""} / ${period}`}
              </button>

              <p
                className="text-muted text-center mt-3 mb-0"
                style={{ fontSize: "0.85rem" }}
              >
                Your card is charged {price ?? ""}
                {price ? ` ${currency}` : ""} today and every {period}{" "}
                thereafter until cancelled; cancel any time from this page.
              </p>
            </div>
            )}
          </div>
        </div>

        {/* Side column */}
        <div className="col-lg-4">
          <SideCard title="PAYMENT METHOD" dashed={!defaultCard}>
            {defaultCard ? (
              <>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold text-uppercase"
                    style={{
                      minWidth: 56,
                      height: 40,
                      borderRadius: 8,
                      background: "#1D2939",
                      color: "#FFFFFF",
                      fontSize: "0.72rem",
                      padding: "0 8px",
                    }}
                  >
                    {defaultCard.brand ?? "Card"}
                  </span>
                  <div>
                    <div className="fw-bold" style={{ color: "#101828" }}>
                      {"\u2022\u2022\u2022\u2022"} {defaultCard.last4}
                    </div>
                    {defaultCard.exp_month && defaultCard.exp_year ? (
                      <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                        Expires{" "}
                        {String(defaultCard.exp_month).padStart(2, "0")} /{" "}
                        {defaultCard.exp_year}
                      </div>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn w-100 py-2 rounded-3 fw-semibold"
                  style={{
                    background: "#FFFFFF",
                    color: PURPLE,
                    border: `1px solid ${BORDER}`,
                  }}
                  onClick={onAddPaymentMethod}
                >
                  Update payment method
                </button>
              </>
            ) : (
              <>
                <p className="mb-3" style={{ color: "#344054" }}>
                  No card on file. Add one to start your subscription.
                </p>
                <button
                  type="button"
                  className="btn w-100 py-2 rounded-3 fw-semibold"
                  style={{
                    background: "#FFFFFF",
                    color: PURPLE,
                    border: `1px solid ${BORDER}`,
                  }}
                  onClick={onAddPaymentMethod}
                >
                  Add payment method
                </button>
              </>
            )}
          </SideCard>

          <SideCard
            title={invoices.length ? "RECENT INVOICES" : "INVOICES"}
            action={
              invoices.length ? (
                <button
                  type="button"
                  className="btn p-0 fw-semibold"
                  style={{ color: PURPLE, background: "none", border: "none" }}
                  onClick={onViewInvoices}
                >
                  View all
                </button>
              ) : null
            }
          >
            {invoices.length ? (
              <div>
                {invoices.slice(0, 3).map((invoice, index) => (
                  <div
                    key={invoice.id ?? index}
                    className="d-flex align-items-center justify-content-between gap-2 py-2"
                    style={{
                      borderTop: index === 0 ? "none" : `1px solid ${BORDER}`,
                    }}
                  >
                    <span style={{ color: "#344054" }}>
                      {formatDate(invoice.created) ?? "—"}
                    </span>
                    <span className="d-flex align-items-center gap-2">
                      <span className="fw-bold" style={{ color: "#101828" }}>
                        {formatPrice(
                          invoice.amount_paid ?? invoice.amount_due,
                          invoice.currency,
                        ) ?? "—"}
                      </span>
                      <span
                        className="rounded-pill px-2 py-1"
                        style={{
                          background:
                            invoice.status === "paid" ? "#ECFDF3" : "#FFFAEB",
                          color:
                            invoice.status === "paid" ? "#027A48" : "#B54708",
                          fontSize: "0.72rem",
                        }}
                      >
                        {invoice.status === "paid" ? "Paid" : invoice.status}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mb-0" style={{ color: "#344054" }}>
                Nothing billed yet. Invoices appear here after your first
                payment.
              </p>
            )}
          </SideCard>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionOverview;
